import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { getBeneficiaries, getBeneficiary } from '../api/beneficiaryActions';
import { IncentivesMarketBeneficiary } from '../types';

const mapOption = (b: IncentivesMarketBeneficiary): CustomAsyncSelectOption => ({
  value: b._id,
  label: `${b.cif || ''} | ${b.name}`,
  name: b.name,
  cif: b.cif,
  email: b.email,
  image: b.image,
  sharesCount: b.sharesCount as any,
  address: b.address,
});

export const useGetBeneficiaryOptions = ({
  societyId,
  beneficiaryId,
  items,
}: {
  societyId?: string;
  beneficiaryId?: string;
  items?: number;
}) => {
  const [selectedBeneficiaryOption, setSelectedBeneficiaryOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [beneficiaryOptions, setBeneficiaryOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchData = useCallback(async () => {
    if (!societyId) return;
    setIsLoading(true);
    try {
      if (beneficiaryId) {
        const b = await store.dispatch(getBeneficiary(beneficiaryId));
        setSelectedBeneficiaryOption(mapOption(b));
      } else {
        const res: any = await store.dispatch(
          getBeneficiaries(societyId, items ? { items } : {})
        );
        setBeneficiaryOptions((res?.data || []).map(mapOption));
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [societyId, beneficiaryId, items]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchBeneficiaryByTerm = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || !societyId) return;

      try {
        const res: any = await store.dispatch(
          getBeneficiaries(societyId, { searchTerm, items: 20 })
        );
        return (res?.data || []).map(mapOption);
      } catch {
        setIsError(true);
      }
    },
    [societyId]
  );

  return {
    data: { beneficiaryOptions, selectedBeneficiaryOption },
    actions: {
      handleChangeBeneficiaryOption: setSelectedBeneficiaryOption,
      handleSearchBeneficiaryByTerm,
    },
    isLoading,
    isError,
  };
};
